/**
 * Camera cuts suggested from who's speaking (see utils/speakerActivity.ts).
 *
 * The rules, as an editor would cut a conversation:
 *  - cut to the speaker's angle a moment (0.25 s) before they start
 *  - hold every shot for at least 2.5 s
 *  - go to the wide for crosstalk or laughter, and during long pauses
 *  - don't cut for short interjections ("yeah", "right"): only talk of 1.5 s
 *    or more gets a shot of its own
 *  - never cut to an angle with no footage there (cut to it once its footage
 *    starts, if its person is still talking), and leave an angle when its
 *    footage ends
 *  - no cut that couldn't be held before the end
 *
 * Then, how the suggestions meet the creator's own cuts: replace them all, or
 * keep them and only fill stretches without any.
 */

import { ActivityRun, SEVERAL_LABEL, SILENCE_LABEL, isAngleLabel } from '../../utils/speakerActivity';

export interface SuggestedCut {
  /** Seconds on the timeline. */
  time: number;
  angleKey: string;
}

export interface CutRules {
  /** Cut this long before a speaker starts. */
  leadS: number;
  /** Hold every shot at least this long. */
  minHoldS: number;
  /** Talk shorter than this (an interjection) never gets a shot of its own. */
  minTurnS: number;
  /** Silence at least this long goes to the wide... */
  longPauseS: number;
  /** ...after this long on the last speaker. */
  pauseHoldS: number;
  /** Crosstalk or laughter at least this long goes to the wide. */
  minSeveralS: number;
}

export const DEFAULT_CUT_RULES: CutRules = {
  leadS: 0.25,
  minHoldS: 2.5,
  minTurnS: 1.5,
  longPauseS: 2.5,
  pauseHoldS: 0.75,
  minSeveralS: 1,
};

export interface PlanCutsInput {
  runs: ActivityRun[];
  frameS: number;
  /** Length of the timeline, seconds. */
  durationS: number;
  angleKeys: string[];
  /** Shown for crosstalk, laughter and long pauses; the master unless the creator picks another. */
  wideKey: string;
  /** Shown before the first cut. */
  masterKey: string;
  hasFootage: (angleKey: string, seconds: number) => boolean;
  rules?: Partial<CutRules>;
}

interface Turn {
  /** An angle key, or null for the wide. */
  key: string | null;
  start: number;
  end: number;
  /** Seconds of the speaker's own talk in it. */
  talk: number;
}

const EPSILON = 1e-3;

/** Turns: one speaker's talk with the pauses inside it, or a stretch for the wide. */
const turnsFrom = (runs: ActivityRun[], frameS: number, rules: CutRules): Turn[] => {
  const turns: Turn[] = [];
  const add = (key: string | null, start: number, end: number, talk: number) => {
    const last = turns[turns.length - 1];
    if (last && last.key === key) {
      last.end = end;
      last.talk += talk;
    } else {
      turns.push({ key, start, end, talk });
    }
  };
  runs.forEach((run) => {
    const start = run.start * frameS;
    const end = (run.start + run.length) * frameS;
    const length = end - start;
    if (isAngleLabel(run.label)) add(run.label, start, end, length);
    else if (run.label === SEVERAL_LABEL && length >= rules.minSeveralS - EPSILON) add(null, start, end, 0);
    else if (run.label === SILENCE_LABEL && length >= rules.longPauseS - EPSILON) add(null, start + rules.pauseHoldS, end, 0);
    // Short silences, short crosstalk and stretches without sound change nothing.
  });

  // Interjections lose their shot; what's either side of them joins up.
  const kept: Turn[] = [];
  turns.forEach((turn) => {
    if (turn.key !== null && turn.talk < rules.minTurnS - EPSILON) return;
    const last = kept[kept.length - 1];
    if (last && last.key === turn.key) {
      last.end = turn.end;
      last.talk += turn.talk;
    } else {
      kept.push({ ...turn });
    }
  });
  return kept;
};

/**
 * The cuts to make, in time order, each to an angle other than the one
 * already showing. The program starts on the master.
 */
export const planSuggestedCuts = (input: PlanCutsInput): SuggestedCut[] => {
  const rules: CutRules = { ...DEFAULT_CUT_RULES, ...(input.rules || {}) };
  const { durationS, angleKeys, wideKey, masterKey, hasFootage, frameS } = input;
  if (!(durationS > 0) || !(frameS > 0) || angleKeys.length < 2) return [];
  const footage = (key: string, at: number) => angleKeys.includes(key) && hasFootage(key, Math.min(durationS, at + EPSILON));

  /** The angle to show for `key` at `at`: it, else the wide, else the master, else any with footage. */
  const resolve = (key: string, at: number): string | null => {
    const order = [key, wideKey, masterKey].concat(angleKeys);
    for (let i = 0; i < order.length; i += 1) if (footage(order[i], at)) return order[i];
    return null;
  };

  const events = turnsFrom(input.runs, frameS, rules).map((turn) => ({
    time: turn.key === null ? turn.start : Math.max(0, turn.start - rules.leadS),
    key: turn.key ?? wideKey,
    until: turn.end,
  }));
  /** Adds an event in time order (after any at the same moment). */
  const insertEvent = (event: { time: number; key: string; until: number }, from: number) => {
    let at = from;
    while (at < events.length && events[at].time <= event.time) at += 1;
    events.splice(at, 0, event);
  };

  const planned: SuggestedCut[] = [];
  let current = masterKey;
  let shotStart = 0;
  for (let i = 0; i < events.length; i += 1) {
    const event = events[i];
    let at = event.time;
    if (planned.length === 0 && at < rules.minHoldS) at = 0;
    else at = Math.max(at, shotStart + rules.minHoldS);
    // A newer change comes before this one could be made.
    if (i + 1 < events.length && events[i + 1].time <= at + EPSILON) continue;
    // The shot couldn't be held before the end.
    if (at > durationS - rules.minHoldS + EPSILON) break;
    const target = resolve(event.key, at);
    if (target && target !== event.key) {
      // The speaker's camera hasn't started yet: try again once it has, while they're still talking.
      for (let frame = Math.floor(at / frameS + EPSILON) + 1; frame * frameS < event.until - rules.minHoldS; frame += 1) {
        const later = frame * frameS;
        if (footage(event.key, later)) {
          insertEvent({ time: later, key: event.key, until: event.until }, i + 1);
          break;
        }
      }
    }
    if (!target || target === current) continue;
    planned.push({ time: at, angleKey: target });
    current = target;
    shotStart = at;
  }

  // Leave an angle when its footage runs out, whatever the hold.
  const cuts: SuggestedCut[] = [];
  current = masterKey;
  let next = 0;
  const frames = Math.floor(durationS / frameS);
  for (let k = 0; k <= frames; k += 1) {
    const at = k * frameS;
    while (next < planned.length && planned[next].time <= at + EPSILON) {
      if (planned[next].angleKey !== current) {
        cuts.push(planned[next]);
        current = planned[next].angleKey;
      }
      next += 1;
    }
    if (at < durationS - EPSILON && !footage(current, at)) {
      const replacement = resolve(wideKey, at);
      if (replacement && replacement !== current) {
        cuts.push({ time: at, angleKey: replacement });
        current = replacement;
      }
    }
  }
  return cuts;
};

// ---------------------------------------------------------------------------
// Meeting the creator's own cuts
// ---------------------------------------------------------------------------

export type ApplyMode = 'replace' | 'fill';

/** A stretch this long with no cut of the creator's is filled in "fill" mode. */
export const MIN_GAP_S = 30;

export interface ExistingCut {
  id: string;
  /** Seconds. */
  time: number;
  angleKey: string;
}

export interface ApplyPlan {
  add: SuggestedCut[];
  removeIds: string[];
}

/** The angle showing at `at` given cuts in time order (a cut at `at` counts). */
const programAt = (cuts: Array<{ time: number; angleKey: string }>, at: number, masterKey: string) => {
  let key = masterKey;
  for (let i = 0; i < cuts.length && cuts[i].time <= at + EPSILON; i += 1) key = cuts[i].angleKey;
  return key;
};

/**
 * What to add and remove. "replace": every cut of the creator's goes, the
 * suggestions come in. "fill": the creator's cuts stay; suggestions go only
 * where they haven't cut for MIN_GAP_S or more, at least a hold away from
 * their cuts, starting with the angle the suggestions show there.
 */
export const planApply = ({
  suggested,
  existing,
  mode,
  durationS,
  masterKey,
  minHoldS = DEFAULT_CUT_RULES.minHoldS,
}: {
  suggested: SuggestedCut[];
  existing: ExistingCut[];
  mode: ApplyMode;
  durationS: number;
  masterKey: string;
  minHoldS?: number;
}): ApplyPlan => {
  const sortedSuggested = suggested.slice().sort((a, b) => a.time - b.time);
  if (mode === 'replace' || existing.length === 0) {
    return { add: sortedSuggested, removeIds: existing.map((cut) => cut.id) };
  }
  const mine = existing.slice().sort((a, b) => a.time - b.time);
  const gaps: Array<[number, number]> = [];
  if (mine[0].time >= MIN_GAP_S) gaps.push([0, mine[0].time - minHoldS]);
  for (let i = 0; i + 1 < mine.length; i += 1) {
    if (mine[i + 1].time - mine[i].time >= MIN_GAP_S) gaps.push([mine[i].time + minHoldS, mine[i + 1].time - minHoldS]);
  }
  const last = mine[mine.length - 1];
  if (durationS - last.time >= MIN_GAP_S) gaps.push([last.time + minHoldS, durationS]);

  const add: SuggestedCut[] = [];
  gaps.forEach(([from, to]) => {
    if (!(to > from)) return;
    let showing = programAt(mine, from, masterKey);
    const want = programAt(sortedSuggested.filter((cut) => cut.time < from - EPSILON), from, masterKey);
    const inside = sortedSuggested.filter((cut) => cut.time >= from - EPSILON && cut.time < to);
    if (want !== showing && !(inside.length && inside[0].time <= from + EPSILON)) {
      add.push({ time: from, angleKey: want });
      showing = want;
    }
    inside.forEach((cut) => {
      if (cut.angleKey === showing) return;
      add.push(cut);
      showing = cut.angleKey;
    });
  });
  return { add, removeIds: [] };
};

export interface PreviewSegment {
  id: string;
  angleKey: string;
  start: number;
  end: number;
}

/**
 * The shots the added cuts would start, each until the next cut (the
 * creator's or another suggestion), for drawing the preview.
 */
export const previewSegments = (plan: ApplyPlan, existing: ExistingCut[], durationS: number): PreviewSegment[] => {
  const removed = new Set(plan.removeIds);
  const all = existing
    .filter((cut) => !removed.has(cut.id))
    .map((cut) => ({ time: cut.time, angleKey: cut.angleKey, added: false }))
    .concat(plan.add.map((cut) => ({ time: cut.time, angleKey: cut.angleKey, added: true })))
    .sort((a, b) => a.time - b.time || Number(a.added) - Number(b.added));
  const segments: PreviewSegment[] = [];
  all.forEach((cut, index) => {
    if (!cut.added) return;
    const end = index + 1 < all.length ? all[index + 1].time : durationS;
    if (end > cut.time) segments.push({ id: `suggested:${segments.length}`, angleKey: cut.angleKey, start: cut.time, end });
  });
  return segments;
};
